/* eslint-disable formatjs/no-literal-string-in-jsx */
'use client';

import { useState, type ReactNode } from 'react';

import { AnimatedSticker } from '../animated-sticker';
import { VerifiedText } from '../typography/verified-text';
import { SheetHeader, SheetTitle } from '~/components/ui';
import { RarityBadge } from '../rarity-badge/rarity-badge';
import { MintIndicator } from '~/module/mint/mint-indicator';
import type { CollectionResponseDtoRarity } from '~/lib/api';

interface StickerData {
  fileName: string;
  id?: string;
}

export interface StickerSheetContentProps {
  title: string;
  brandName: string;
  rarity: CollectionResponseDtoRarity;
  stickers: StickerData[];
  totalSupply?: number | string;
  showCount?: boolean;
  availableCount?: number;
  bottomContent?: ReactNode;
  numberInCollection?: string;
  showMintIndicator?: boolean;
}

export const StickerSheetContent = ({
  title,
  brandName,
  rarity,
  stickers,
  totalSupply,
  showCount = false,
  availableCount = 0,
  numberInCollection,
  bottomContent,
  showMintIndicator,
}: StickerSheetContentProps) => {
  const [activeImage, setActiveImage] = useState<number>(0);

  return (
    <>
      <SheetTitle />
      <div className="grid grid-rows-[max-content_1fr_max-content] max-h-[90vh]">
        <div>
          <SheetHeader className="flex flex-row items-center justify-start w-full mt-[31px] gap-2">
            {showMintIndicator ? <MintIndicator /> : null}
            <RarityBadge className="text-[11px]" rarity={rarity} textClassName="text-[11px]" />
            {showCount && totalSupply ? (
              <div className="flex font-bold text-sm">
                <span className="text-white/60">{availableCount}</span>
                <span>{`/${totalSupply}`}</span>
              </div>
            ) : null}
          </SheetHeader>

          <div className="flex flex-col gap-2 mt-4 opacity-100">
            <span className="text-3xl font-bold text-white">{title}</span>
            <div className="flex gap-2">
              <span className="text-sm flex font-bold">
                <VerifiedText svgClassName="size-[14px]" text={brandName} />
              </span>
              {numberInCollection ? (
                <span className="text-sm flex font-bold text-white/80">#{numberInCollection}</span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="grid overflow-hidden h-full pt-8">
          <AnimatedSticker autoplay className="bg-white/5" leading loop src={stickers[activeImage]?.fileName ?? ''} />
          <div className="ml-[10%] mt-4 flex gap-2 overflow-x-auto scrollbar-hide mb-6">
            {stickers.map((sticker, index) => (
              <div
                key={sticker.id ?? `${sticker.fileName}-${index}`}
                onClick={() => {
                  setActiveImage(index);
                }}
              >
                <div className="size-[68.15px]">
                  <AnimatedSticker autoplay className="bg-white/5" leading={false} loop src={sticker.fileName} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>{bottomContent}</div>
      </div>
    </>
  );
};
